<script>
  import { onMount } from "svelte";
  import { progress, load, saveNow } from "./lib/state.svelte.js";
  import { myRating, ratingInfo, solvedCount } from "./lib/economy.js";
  import { eraForEvo } from "../data/journeyEngine.js";
  import { fmtEra, fmtFuture } from "./lib/format.js";
  import { organismInner, defaultOrganism } from "../data/organism.js";
  import { ensureAudio, playSound } from "./lib/audio.js";
  import { setupCanvas } from "./lib/effects.js";
  import { nav, NAV_ITEMS, go } from "./lib/nav.svelte.js";
  import { getAuthState, installBridge, bridgeReady, signOut } from "./lib/online.js";
  import { initInstall, registerServiceWorker } from "./lib/install.svelte.js";
  import { initAuthState } from "./lib/authState.svelte.js";
  import { seedFromAttempts } from "./lib/missed.svelte.js";

  import Journey from "./views/Journey.svelte";
  import Learn from "./views/Learn.svelte";
  import Problem from "./views/Problem.svelte";
  import Dashboard from "./views/Dashboard.svelte";
  import Leaderboard from "./views/Leaderboard.svelte";
  import Account from "./views/Account.svelte";
  import InstallPrompt from "./views/InstallPrompt.svelte";

  let ready = $state(false);
  let menuOpen = $state(false);
  let authTick = $state(0);

  const rating = $derived(progress.evo >= 0 ? myRating() : 0);
  const rank = $derived(ratingInfo(rating));
  const here = $derived(eraForEvo(progress.evo));
  const eraText = $derived(here.isFuture ? fmtFuture(here.futureMa) : fmtEra(here.ma));
  const auth = $derived.by(() => {
    authTick;
    return getAuthState();
  });

  /* ログインしたら、それまでの挑戦履歴から「間違えた問題」を組み立てる。
     登録前に間違えた問題も最初から一覧に並ぶので、機能が空のまま始まらない。 */
  let seeded = false;
  $effect(() => {
    if (!ready || !auth.user || seeded) return;
    seeded = true;
    seedFromAttempts();
  });

  onMount(() => {
    installBridge(() => authTick++);
    initAuthState();
    load();
    if (!progress.organism) progress.organism = defaultOrganism();
    bridgeReady();
    setupCanvas();
    initInstall();
    registerServiceWorker();
    ready = true;

    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    window.addEventListener("beforeunload", saveNow);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("beforeunload", saveNow);
    };
  });

  function navigate(view) {
    menuOpen = false;
    ensureAudio();
    playSound("click");
    go(view);
  }

  function toggleSound() {
    progress.soundOn = !progress.soundOn;
    saveNow();
    if (progress.soundOn) {
      ensureAudio();
      playSound("click");
    }
  }
</script>

{#if !ready}
  <div id="boot">
    <div class="spin"></div>
    <p>🧬 MolCellQuest を起動中…</p>
  </div>
{:else}
  <header class:menu-open={menuOpen}>
    <button id="logo" onclick={() => navigate("journey")}>🧬 MolCellQuest</button>

    <span class="rank-chip hide-sm">
      <span class="rank-dot" style="background:{rank.color}"></span>
      {rank.em}
      {rank.name}
    </span>

    <span class="stat">{here.era.em} <b>{eraText}</b></span>
    <span class="stat hide-sm">🔥連続 <b>{progress.streak}</b>日</span>
    <span class="stat hide-sm">✅ <b>{solvedCount()}</b>問</span>

    <span class="spacer"></span>

    <nav>
      {#each NAV_ITEMS as item (item.id)}
        <button class:active={nav.view === item.id} onclick={() => navigate(item.id)}>
          {item.em}
          {item.label}
        </button>
      {/each}
    </nav>

    <button
      class="icon-btn"
      onclick={toggleSound}
      title={progress.soundOn ? "効果音を消す" : "効果音を鳴らす"}
      aria-label={progress.soundOn ? "効果音を消す" : "効果音を鳴らす"}
    >
      {progress.soundOn ? "🔊" : "🔇"}
    </button>

    <div class="profile-wrap">
      <button
        class="avatar-round"
        onclick={() => (menuOpen = !menuOpen)}
        title="プロフィール"
        aria-label="プロフィール"
      >
        <svg viewBox="0 0 100 100">{@html organismInner(here.era.form, progress.organism)}</svg>
      </button>

      {#if menuOpen}
        <div
          class="menu-backdrop"
          onclick={() => (menuOpen = false)}
          onkeydown={(e) => e.key === "Escape" && (menuOpen = false)}
          role="button"
          tabindex="-1"
          aria-label="メニューを閉じる"
        ></div>
        <div class="profile-menu">
          <div class="pm-head">
            <div class="pm-name">{progress.nickname || "ななしの生物"}</div>
            <div class="tiny">{here.era.em} {here.era.name}</div>
          </div>
          <button onclick={() => navigate("account")}>⚙️ アカウント・設定</button>
          <button onclick={() => navigate("dash")}>📊 学習の記録</button>
          {#if auth.user}
            <button
              onclick={() => {
                menuOpen = false;
                signOut();
              }}>🚪 ログアウト</button
            >
          {/if}
        </div>
      {/if}
    </div>
  </header>

  <main class="view-enter">
    {#key nav.view}
      {#if nav.view === "journey"}
        <Journey />
      {:else if nav.view === "learn"}
        <Learn />
      {:else if nav.view === "prob"}
        <Problem />
      {:else if nav.view === "dash"}
        <Dashboard />
      {:else if nav.view === "leaderboard"}
        <Leaderboard />
      {:else if nav.view === "account"}
        <Account />
      {/if}
    {/key}

    {#if nav.view !== "journey"}
      <footer class="site-footer">
        MolCellQuest — 46億年の旅で学ぶ分子生物学 · 進捗はこの端末のブラウザに保存されます
      </footer>
    {/if}
  </main>

  <nav class="bottom-nav">
    {#each NAV_ITEMS as item (item.id)}
      <button class:active={nav.view === item.id} onclick={() => navigate(item.id)}>
        <span class="bn-em">{item.em}</span>
        <span>{item.short}</span>
      </button>
    {/each}
  </nav>

  <InstallPrompt />
{/if}

<canvas id="fx"></canvas>
<div id="toast"></div>
